import { useMemo, useState } from "react";
import { useNavigate, useParams, useMatch } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Button,
  Checkbox,
  IconButton,
  LoadingSkeleton,
  LoadingShim,
  Snackbar,
  Table,
  useBreakpoints,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import { entities } from "byzantine";
import { useResolvedBulkPayment } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import {
  useFetchPaginatedPaymentActivity,
  useFormattedPaginatedPaymentActivity,
  usePaymentActivityTotal,
  usePaymentActivityById,
  formatPaymentActivity,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { useAllRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import PageLayout from "../../../src/components/PageLayout";
import PageNavigation from "../../page_navigation/PageNavigation";
import { SudoProvider } from "../../SudoContext";
import { SearchBar, EmptyState, PaymentActivityRow } from "../common";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import { PaymentActivityDrawer } from "../Individual";
import usePaymentActivityContainer from "../hooks";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getBulkReversalSelectionRoute,
  getBulkReverseFromSelectionRoute,
} from "../routes";
import BulkReversalCheckboxCell from "./BulkReversalCheckboxCell";
import BulkReverseFlow from "./BulkReverseFlow";
import headerStyles from "./BulkSelectReversalScreen.module.scss";

type SelectReversalRouteConfig = {
  // url param key for the id in this route context ("bulkPaymentId" or "fileImportId")
  paramKey: string;
  getSelectionRoute: (id: string) => string;
  getReverseFromSelectionRoute: (id: string) => string;
  backRoute: (id: string) => string;
  // where onComplete navigates after a successful reversal
  listRoute: string;
};

const BulkSelectReversalScreen = ({
  routeConfig,
}: {
  routeConfig?: SelectReversalRouteConfig;
} = {}) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { m } = useBreakpoints();

  // default to bulk routing
  const config: SelectReversalRouteConfig = routeConfig ?? {
    paramKey: "bulkPaymentId",
    getSelectionRoute: (id) => getBulkReversalSelectionRoute(id),
    getReverseFromSelectionRoute: (id) => getBulkReverseFromSelectionRoute(id),
    backRoute: (id) => `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${id}`,
    listRoute: `/${PAYMENT_ACTIVITY_ROUTE}/bulk`,
  };
  const params = useParams<Record<string, string>>();
  const bulkPaymentId = params[config.paramKey];

  // the reverse modal is mounted as a child of this screen (so the table stays
  // behind it) when the nested /reverse sub-route is active
  const reverseMatch = useMatch(
    `${config.getReverseFromSelectionRoute(`:${config.paramKey}`)}/*`
  );
  const isReversing = Boolean(reverseMatch);

  // resolve the bulk name from the store first, fall back to a fetch for deep links
  const bulkPayment = useResolvedBulkPayment({
    bulkPaymentId: bulkPaymentId as API.BulkPaymentId,
  });

  const baseQueryString = useMemo(
    () =>
      `${FILTER_CONFIG[FilterChips.BULK_PAYMENT].queryKey}=${bulkPaymentId}`,
    [bulkPaymentId]
  );

  const {
    state,
    formattedPaginatedPayments,
    paymentsLoading,
    bootstrapLoading,
    pagination,
    handleSearchChange,
    handleReset,
  } = usePaymentActivityContainer({
    useFetchPaginated: useFetchPaginatedPaymentActivity,
    useFormattedPaginated: useFormattedPaginatedPaymentActivity,
    useTotal: usePaymentActivityTotal,
    useBootstrap: entities.paymentActivity.bootstrap.useBootstrap,
    baseQueryString,
  });

  const payments = formattedPaginatedPayments as API.FormattedPaymentActivity[];

  // selected payment uuids (for reversal). only eligible payments can be selected
  // tracked by uuid so selection persists across search + pagination
  // reversing hidden selections is fine — the reverse modal's confirmation page
  // shows exactly what will be reversed before submitting
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());

  const selectablePayments = useMemo(
    () => payments.filter((p) => p.isReversible),
    [payments]
  );
  // total selected across all pages/searches (drives the action bar)
  const selectedCount = selectedUuids.size;
  // header checkbox state reflects only the currently-visible eligible rows
  const allSelectableSelected =
    selectablePayments.length > 0 &&
    selectablePayments.every((p) => selectedUuids.has(p.uuid));
  const someSelected =
    selectablePayments.some((p) => selectedUuids.has(p.uuid)) &&
    !allSelectableSelected;

  // resolve the selected payment objects from the redux store
  const paymentsById = usePaymentActivityById();
  const recipients = useAllRecipients();
  const accounts = useAllAccounts();
  const selectedPayments = useMemo(
    () =>
      [...selectedUuids]
        .map((uuid) => paymentsById[uuid])
        .filter(Boolean)
        .map((p) =>
          formatPaymentActivity(p as API.PaymentActivity, recipients, accounts)
        )
        .filter((p) => Boolean(p.isReversible)),
    [selectedUuids, paymentsById, recipients, accounts]
  );

  const handleToggleRow = (uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  // header select-all only toggles the currently-visible eligible rows so it
  // never drops selections made on other pages / searches
  const handleToggleSelectAll = () => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (selectablePayments.every((p) => next.has(p.uuid))) {
        selectablePayments.forEach((p) => next.delete(p.uuid));
      } else {
        selectablePayments.forEach((p) => next.add(p.uuid));
      }
      return next;
    });
  };

  const handleClearSelection = () => setSelectedUuids(new Set());

  const handleReverseSelected = () => {
    navigate(config.getReverseFromSelectionRoute(bulkPaymentId as string));
  };

  // drawer is driven by local state (not the URL) so we stay on this screen
  // track the selected payment by uuid (not list index) so the drawer keeps
  // pointing at the right payment if the list re-paginates or filters
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const selectedIndex = selectedUuid
    ? payments.findIndex((p) => p.uuid === selectedUuid)
    : -1;
  const selectedPayment = selectedIndex >= 0 ? payments[selectedIndex] : null;
  const isDrawerOpen = selectedPayment !== null;

  const handleCloseDrawer = () => setSelectedUuid(null);
  const handleOnNext = () => {
    if (selectedIndex >= 0 && selectedIndex < payments.length - 1) {
      setSelectedUuid(payments[selectedIndex + 1].uuid);
    }
  };
  const handleOnPrev = () => {
    if (selectedIndex > 0) {
      setSelectedUuid(payments[selectedIndex - 1].uuid);
    }
  };

  const handleBack = () => {
    navigate(config.backRoute(bulkPaymentId as string));
  };

  const renderPaymentsTable = () => {
    if (paymentsLoading) return <LoadingShim isLoading />;
    if (payments.length === 0) {
      return <EmptyState state={state} handleReset={handleReset} />;
    }

    return (
      <>
        <Table
          colVisibility={[
            "*", // checkbox
            "*", // description
            "m", // status only displayed on >M screens
            "l", // account only displayed on >L screens
            "*", // amount
          ]}
          colLayout={{
            s: "auto 1fr min-content", // [checkbox] [description] [amount]
            m: "auto 1fr 28% 15%", // [checkbox] [description] [status] [amount]
            l: "auto 1fr 20% 25% 10%", // [checkbox] [description] [status] [account] [amount]
          }}
        >
          {m ? (
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  <Checkbox
                    kind="table"
                    label={l10n.getString("bulk-reversal-select-all-label")}
                    checked={allSelectableSelected}
                    indeterminate={someSelected}
                    isDisabled={selectablePayments.length === 0}
                    onChange={handleToggleSelectAll}
                  />
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <span className={cc({ "margin--left--l": m })}>
                    {l10n.getString("label-description")}
                  </span>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("payment-activity-label-status")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("label-account")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("label-amount")}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
          ) : null}
          <Table.Body>
            {payments.map((item) => (
              <PaymentActivityRow
                key={`table-row__${item.uuid}`}
                payment={item}
                onRowClick={() => setSelectedUuid(item.uuid)}
                selectionCell={
                  <BulkReversalCheckboxCell
                    isSelectable={Boolean(item.isReversible)}
                    isSelected={selectedUuids.has(item.uuid)}
                    onToggle={() => handleToggleRow(item.uuid)}
                  />
                }
              />
            ))}
          </Table.Body>
        </Table>
        <PageNavigation
          pagination={pagination}
          marginClasses="margin--all--l"
        />
      </>
    );
  };

  return (
    <LoadingSkeleton isLoading={bootstrapLoading}>
      <PageLayout>
        {/*
         * single wrapper div so the Table (display: grid) is not a direct child
         * of PageLayout, whose layout-single rule forces children to
         * display: flex; flex-direction: column and would break the grid columns
         */}
        <div>
          <div className={cc(headerStyles.header, "margin--top--xl")}>
            <div className={headerStyles.backButton}>
              <Button
                kind="plain"
                onClick={handleBack}
                testId="back-button"
                ariaLabel={l10n.getString("button-back")}
                startIcon="chevron-left"
              />
            </div>
            <h1 className="fontColor--heading">{bulkPayment?.title}</h1>
            <div className="fontColor--secondary margin--top--m fontSize--l">
              {l10n.getString("bulk-reversal-select-payments-subtitle")}
            </div>
          </div>
          <div className="margin--top--m margin--bottom--m">
            <SearchBar
              handleSearchChange={handleSearchChange}
              searchValue={state.searchValue}
              placeholder={l10n.getString("payment-activity-search-label")}
            />
          </div>
          {renderPaymentsTable()}
        </div>
      </PageLayout>
      <div className={headerStyles.stickyContainer}>
        <Snackbar isActive={selectedCount > 0}>
          <span className={headerStyles.selectionSummary}>
            <Snackbar.Text>
              {l10n.getString("bulk-reversal-selected-count", {
                totalSelected: selectedCount,
              })}
            </Snackbar.Text>
            <IconButton
              kind="action"
              name="x"
              textSize="l"
              label={l10n.getString("label-close")}
              onClick={handleClearSelection}
            />
          </span>
          <Snackbar.Divider />
          <Snackbar.ButtonGroup>
            <Button
              kind="primary"
              label={l10n.getString("bulk-reversal-reverse-selected")}
              testId="reverse-selected-button"
              onClick={handleReverseSelected}
            />
          </Snackbar.ButtonGroup>
        </Snackbar>
      </div>
      {isDrawerOpen && selectedPayment ? (
        <PaymentActivityDrawer
          isOpen={isDrawerOpen}
          handleClose={handleCloseDrawer}
          handleOnNext={handleOnNext}
          handleOnPrev={handleOnPrev}
          // reverse happens via the selection flow on this screen, so suppress footer actions
          hideFooterActions
          payment={selectedPayment}
          paymentUuid={
            selectedPayment.uuid as API.ScheduledACHPaymentId | API.ACHPaymentId
          }
        />
      ) : null}
      {isReversing && bulkPaymentId ? (
        <SudoProvider
          basename={config.getReverseFromSelectionRoute(bulkPaymentId)}
        >
          <BulkReverseFlow
            bulkPaymentId={bulkPaymentId}
            reverseRoute={config.getReverseFromSelectionRoute(bulkPaymentId)}
            dismissRoute={config.getSelectionRoute(bulkPaymentId)}
            selectedUuids={[...selectedUuids]}
            preloadedPayments={selectedPayments}
            onComplete={() => {
              navigate(config.listRoute);
            }}
          />
        </SudoProvider>
      ) : null}
    </LoadingSkeleton>
  );
};

export default BulkSelectReversalScreen;
